import { StateCreator } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';

import {
  cancelRewardRedemption as restCancelRewardRedemption,
  cancelWebPlanSubscription as restCancelWebPlanSubscription,
  captureRewardRedemptionPayment as restCaptureRewardRedemptionPayment,
  completeRewardRedemption as restCompleteRewardRedemption,
  createRewardCategory as restCreateRewardCategory,
  createRewardRedemption as restCreateRewardRedmption,
  createRewardReferral as restCreateRewardReferral,
  deleteRewardCategory as restDeleteRewardCategory,
  extendRewardRedemptionCompletion as restExtendRewardRedemptionCompletion,
  fulfillPhysicalGoodRedemption as restFulfillPhysicalGoodRedemption,
  getRewardCategories as restGetRewardCategories,
  getRewardOffer as restGetRewardOffer,
  getRewardOffers as restGetRewardOffers,
  getRewardRedemption as restGetRewardRedemption,
  getRewardRedemptions as restGetRewardRedemptions,
  getRewardReferral as restGetRewardReferral,
  getRewardReferralCode as restGetRewardReferralCode,
  getRewardReferrals as restGetRewardReferrals,
  getRewardReferralUser as restGetRewardReferralUser,
  manageWebPlanSubscription as restManageWebPlanSubscription,
  referralCodeValidation as restReferralCodeValidation,
  remindRewardRedemption as restRemindRewardRedemption,
  updateRewardCategory as restUpdateRewardCategory,
} from '../../api/Rest/rewards';
import { ProfileData } from '../../api/utils';
import { I18nLocale, Uuid } from '../../models/Base';
import { Cursor } from '../../models/Repository';
import {
  RewardCategory,
  RewardCategoryRepository,
  RewardOffer,
  RewardOfferJson,
  RewardOfferListField,
  RewardOfferMapType,
  RewardOfferRepository,
  RewardPlaform,
  RewardRedemption,
  RewardRedemptionJson,
  RewardRedemptionListField,
  RewardRedemptionRepository,
  RewardRedemptionTabState,
  RewardReferral,
  RewardReferralRepository,
  RewardReferralType,
  RewardStoreAction,
  RewardStoreState,
} from '../../models/RewardStore';
import { SpaceMembership } from '../../models/SpaceStore';
import {
  createStoreListFieldHook,
  createStoreMapFieldHook,
  StoreLifecycle,
  StoreLifecycleAction,
} from '../../models/Store';
import {
  Profile,
  ProfileMapType,
  ProfileWithMemberships,
  ResetPasswordResult,
  UserListType,
} from '../../models/UserStore';
import { useSessionStore } from '../../stores/SessionStore';
import { useUserStore } from '../../stores/UserStore';
import { createI18nInfo } from '../../utils/i18n';

import * as State from './state';

type Store = StoreLifecycleAction & RewardStoreState & RewardStoreAction;

const lifecycle = new StoreLifecycle('RewardStore');
lifecycle.finishHydration();

const initializer: StateCreator<Store> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      const state = {
        categories: new RewardCategoryRepository(),
        offers: new RewardOfferRepository(),
        redemptions: new RewardRedemptionRepository(),
        referrals: new RewardReferralRepository(),
      };
      state.categories.onElementPrune = onCategoryPrune;
      state.offers.onElementPrune = onOfferPrune;
      state.redemptions.onElementPrune = onRedemptionPrune;
      set(state);
    },
    () => set((state) => ({ ...state })),
  );

  // category

  const getCategories: RewardStoreAction['getCategories'] = (fieldName) => {
    return State.getCategories(get(), fieldName);
  };

  const getCategory: RewardStoreAction['getCategory'] = (rewardCategoryId) =>
    State.getCategory(get(), rewardCategoryId);

  const fetchCategories: RewardStoreAction['fetchCategories'] = async (
    store,
    cursor,
    options,
  ) => {
    const { ok, data } = await restGetRewardCategories(
      { store },
      { limit: 20, cursor },
      options,
    );
    if (ok) {
      const newCursor = data.cursor ?? null;
      set((state) => {
        const categories = data.rewardCategories.map(
          (c) => new RewardCategory(c),
        );
        if (cursor === undefined) {
          State.setCategories(
            state,
            store,
            // default add 'All' category
            RewardCategory.createAll(store),
            ...categories,
          );
        } else {
          State.pushCategories(state, store, ...categories);
        }
        State.setCatergoryCursor(state, store, newCursor);
        return { ...state };
      });
      return {
        shouldFetchMore: !!newCursor && data.rewardCategories.length === 0,
      };
    }
    return { shouldFetchMore: false };
  };

  const createCategory: RewardStoreAction['createCategory'] = async (
    store,
    name,
    options,
  ) => {
    const { ok, data, error } = await restCreateRewardCategory(
      { store },
      {
        i18nRewardCategoryInfo: createI18nInfo(I18nLocale.US, {
          name,
          shortDescription: null,
          description: null,
        }),
      },
      options,
    );
    if (ok) {
      const category = new RewardCategory(data.rewardCategory);
      set((state) => {
        State.pushCategories(state, store, category);
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to create category', error.message);
      return false;
    }
  };

  const updateCategory: RewardStoreAction['updateCategory'] = async (
    store,
    rewardCategoryId,
    body,
    options,
  ) => {
    const { ok, data, error } = await restUpdateRewardCategory(
      { store, rewardCategoryId },
      body,
      options,
    );
    if (ok) {
      const category = new RewardCategory(data.rewardCategory);
      set((state) => {
        State.updateCategory(state, category);
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to update category', error.message);
      return false;
    }
  };

  const deleteCategory: RewardStoreAction['deleteCategory'] = async (
    store,
    rewardCategoryId,
    relocationCategoryId,
    options,
  ) => {
    if (rewardCategoryId === RewardCategory.createAll(store).rewardCategoryId) {
      return false;
    }
    const { ok, error } = await restDeleteRewardCategory(
      { store, rewardCategoryId },
      { relocationCategoryId },
      options,
    );
    if (ok) {
      set((state) => {
        if (relocationCategoryId) {
          const newCategory = State.getCategory(state, relocationCategoryId);
          if (newCategory) {
            const fromFieldName = `${store}_${rewardCategoryId}`;
            const toFieldName = `${store}_${relocationCategoryId}`;
            State.pushOffers(
              state,
              toFieldName,
              ...State.getOffers(state, fromFieldName),
            );
            State.pushOffers(
              state,
              `${toFieldName}_admin`,
              ...State.getOffers(state, `${fromFieldName}_admin`),
            );
          }
        }

        const oldCategory = State.getCategory(state, rewardCategoryId);
        if (oldCategory) State.removeCategory(state, oldCategory);
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to delete category', error.message);
      return false;
    }
  };

  // offer
  const getOffers: RewardStoreAction['getOffers'] = (fieldName) =>
    State.getOffers(get(), fieldName);

  const getOffer: RewardStoreAction['getOffer'] = (rewardOfferId) =>
    State.getOffer(get(), rewardOfferId);

  const handleFetchOffersResult = (
    fieldName: RewardOfferListField,
    oldCursor: Cursor,
    newCursor: Cursor,
    rewardOffers: RewardOfferJson[],
    profiles?: ProfileData[],
  ) => {
    set((state) => {
      const offers = rewardOffers.map((o) => new RewardOffer(o));
      if (oldCursor === undefined) {
        State.setOffers(state, fieldName, ...offers);
      } else {
        State.pushOffers(state, fieldName, ...offers);
      }
      State.setOfferCursor(state, fieldName, newCursor);
      return { ...state };
    });

    if (profiles) {
      useUserStore.getState().addProfilesWithMemberships(
        ProfileMapType.RewardOffer,
        rewardOffers.reduce<Record<Uuid, ProfileWithMemberships>>(
          (acc, offer) => {
            const p = profiles.find(
              ({ profile }) => profile.userId === offer.createdBy,
            );
            if (p) {
              acc[offer.rewardOfferId] = {
                profile: new Profile(p.profile),
                membership: p.membership
                  ? new SpaceMembership(p.membership)
                  : null,
              };
            } else {
              console.warn(
                `missing user profile ${offer.createdBy}, maybe account was deactiviated`,
              );
            }
            return acc;
          },
          {},
        ),
      );
    }

    return {
      shouldFetchMore: !!newCursor && rewardOffers.length === 0,
    };
  };

  const fetchRecentOffers: RewardStoreAction['fetchRecentOffers'] = async (
    store,
    leaderboardId,
    options,
  ) => {
    const allCategory = RewardCategory.createAll(store);
    set((state) => {
      State.pushCategories(state, store, allCategory);
      return { ...state };
    });
    const category = State.getCategory(get(), allCategory.rewardCategoryId);
    if (category) {
      let shouldFetchMore = true;
      const fieldName = `${store}_${category.rewardCategoryId}`;
      do {
        const { ok, data } = await restGetRewardOffers(
          { store },
          {
            offeredTo: useSessionStore.getState().activeUserId ?? '',
            rewardCategoryId: undefined,
            leaderboardId,
            limit: 20,
            cursor: State.getOfferCursor(get(), fieldName),
          },
          options,
        );
        if (ok) {
          shouldFetchMore = handleFetchOffersResult(
            fieldName,
            undefined,
            data.cursor ?? null,
            data.rewardOffers,
          ).shouldFetchMore;
        } else {
          shouldFetchMore = false;
        }
      } while (shouldFetchMore);
    }
  };

  const fetchOffers: RewardStoreAction['fetchOffers'] = async (
    store,
    { rewardCategoryId: _rewardCategoryId, leaderboardId, referralSystem },
    cursor,
    options,
  ) => {
    const allRewardCategoryId =
      RewardCategory.createAll(store).rewardCategoryId;
    const rewardCategoryId =
      _rewardCategoryId === allRewardCategoryId ? undefined : _rewardCategoryId;

    const { ok, data } = await restGetRewardOffers(
      { store },
      {
        offeredTo: useSessionStore.getState().activeUserId ?? undefined,
        rewardCategoryId,
        leaderboardId,
        referralSystem,
        cursor,
        limit: 20,
      },
      options,
    );
    if (ok) {
      return handleFetchOffersResult(
        `${store}_${rewardCategoryId ?? allRewardCategoryId}`,
        cursor,
        data.cursor ?? null,
        data.rewardOffers,
      );
    }

    return { shouldFetchMore: false };
  };

  const fetchOffersByAdmin: RewardStoreAction['fetchOffersByAdmin'] = async (
    store,
    { rewardCategoryId: _rewardCategoryId, leaderboardId },
    cursor,
    options,
  ) => {
    const allRewardCategoryId =
      RewardCategory.createAll(store).rewardCategoryId;
    const rewardCategoryId =
      _rewardCategoryId === allRewardCategoryId ? undefined : _rewardCategoryId;
    const category = State.getCategory(
      get(),
      rewardCategoryId ?? allRewardCategoryId,
    );
    if (category) {
      const { ok, data } = await restGetRewardOffers(
        { store },
        {
          offeredTo: undefined,
          rewardCategoryId,
          leaderboardId,
          cursor,
          limit: 20,
          withProfiles: true,
        },
        options,
      );
      if (ok) {
        return handleFetchOffersResult(
          `${store}_${rewardCategoryId ?? allRewardCategoryId}_admin`,
          cursor,
          data.cursor ?? null,
          data.rewardOffers,
          data.profiles,
        );
      }
    }
    return { shouldFetchMore: false };
  };

  const fetchOffer: RewardStoreAction['fetchOffer'] = async (
    store,
    rewardOfferId,
    options,
  ) => {
    const allRewardCategoryId =
      RewardCategory.createAll(store).rewardCategoryId;
    const offer = State.getOffer(get(), rewardOfferId);
    const fieldName = `${store}_${allRewardCategoryId}_${rewardOfferId}`;
    if (offer) {
      set((state) => {
        State.pushOffers(state, fieldName, offer);
        return { ...state };
      });
    } else {
      const { ok, data } = await restGetRewardOffer(
        { store, rewardOfferId },
        options,
      );
      if (ok) {
        set((state) => {
          State.pushOffers(state, fieldName, new RewardOffer(data.rewardOffer));
          return { ...state };
        });
      }
    }
  };

  const fetchOfferByRedemptionId: RewardStoreAction['fetchOfferByRedemptionId'] =
    async (rewardRedemptionId, store, options) => {
      const redemption = State.getRedemption(get(), rewardRedemptionId);
      if (redemption) {
        const rewardOfferId = redemption.rewardOfferId;
        const offer = State.getOffer(get(), rewardOfferId);
        if (offer) {
          set((state) => {
            State.addOfferToMap(state, rewardRedemptionId, offer);
            return { ...state };
          });
        } else {
          const { ok, data } = await restGetRewardOffer(
            { store, rewardOfferId },
            options,
          );
          if (ok) {
            set((state) => {
              State.addOfferToMap(
                state,
                rewardRedemptionId,
                new RewardOffer(data.rewardOffer),
              );
              return { ...state };
            });
          }
        }
      }
    };

  // redemption
  const getRedemptions: RewardStoreAction['getRedemptions'] = (fieldName) =>
    State.getRedemptions(get(), fieldName);

  const getRedemption: RewardStoreAction['getRedemption'] = (
    rewardRedemptionId,
  ) => State.getRedemption(get(), rewardRedemptionId);

  const getRedemptionsByOfferId: RewardStoreAction['getRedemptionsByOfferId'] =
    (offerId, store) => State.getRedemptions(get(), `${store}_${offerId}`);

  const handleFetchRedemptionResult = (
    fieldName: RewardRedemptionListField,
    oldCursor: Cursor,
    newCursor: Cursor,
    rewardRedemptions: RewardRedemptionJson[],
    rewardOffers: RewardOfferJson[],
    isCompleted: boolean | undefined,
    store: RewardPlaform,
    profiles?: ProfileData[],
  ) => {
    const { redemptions, offers } = rewardRedemptions.reduce<{
      redemptions: RewardRedemption[];
      offers: Record<string, RewardOffer | null>;
    }>(
      (acc, o) => {
        const redemption = new RewardRedemption(o);
        if (
          isCompleted === undefined ||
          redemption.isCompleted() === isCompleted
        ) {
          acc.redemptions.push(redemption);
          if (acc.offers[redemption.rewardOfferId] === undefined) {
            const offer = rewardOffers.find(
              ({ rewardOfferId }) => redemption.rewardOfferId === rewardOfferId,
            );
            if (offer) {
              acc.offers[offer.rewardOfferId] = new RewardOffer(offer);
            } else {
              acc.offers[redemption.rewardOfferId] = null;
            }
          }
        }
        return acc;
      },
      {
        redemptions: [],
        offers: {},
      },
    );
    set((state) => {
      // redemption
      if (oldCursor === undefined) {
        State.setRedemptions(state, fieldName, store, ...redemptions);
      } else {
        State.pushRedemptions(state, fieldName, store, ...redemptions);
      }
      State.setRedemptionCursor(state, fieldName, newCursor);

      // offer
      redemptions.forEach((redemption) => {
        const offer = offers[redemption.rewardOfferId];
        if (offer !== undefined)
          State.addOfferToMap(state, redemption.rewardRedemptionId, offer);
      });
      return { ...state };
    });
    if (profiles) {
      useUserStore.getState().addProfilesWithMemberships(
        ProfileMapType.Redemption,
        redemptions.reduce<Record<Uuid, ProfileWithMemberships>>(
          (acc, redemption) => {
            const offer = offers[redemption.rewardOfferId];
            if (offer) {
              const creator = profiles.find(
                ({ profile }) => profile.userId === offer.createdBy,
              );
              if (creator) {
                acc[offer.rewardOfferId] = {
                  profile: new Profile(creator.profile),
                  membership: creator.membership
                    ? new SpaceMembership(creator.membership)
                    : null,
                };
              }
            }
            const buyer = profiles.find(
              ({ profile }) => profile.userId === redemption.userId,
            );
            if (buyer) {
              acc[redemption.rewardRedemptionId] = {
                profile: new Profile(buyer.profile),
                membership: buyer.membership
                  ? new SpaceMembership(buyer.membership)
                  : null,
              };
            } else {
              console.warn(
                `missing user profile ${redemption.userId}, maybe account was deactiviated`,
              );
            }
            return acc;
          },
          {},
        ),
      );
    }

    return {
      shouldFetchMore: !!newCursor && redemptions.length === 0,
    };
  };

  const fetchRedemptions: RewardStoreAction['fetchRedemptions'] = async (
    store,
    tabState,
    cursor,
    options,
  ) => {
    const { state, isCompleted } = RewardRedemption.tabStateToState(tabState);
    const { ok, data } = await restGetRewardRedemptions(
      { store },
      {
        state,
        redeemedBy: useSessionStore.getState().activeUserId ?? '',
        cursor,
        limit: 20,
      },
      options,
    );
    if (ok) {
      return handleFetchRedemptionResult(
        `${store}_${tabState}`,
        cursor,
        data.cursor ?? null,
        data.rewardRedemptions,
        data.rewardOffers ?? [],
        isCompleted,
        store,
      );
    }

    return { shouldFetchMore: false };
  };

  const fetchRedemptionsByAdmin: RewardStoreAction['fetchRedemptionsByAdmin'] =
    async (store, tabState, cursor, options) => {
      const { state, isCompleted } = RewardRedemption.tabStateToState(tabState);
      const { ok, data } = await restGetRewardRedemptions(
        { store },
        {
          state,
          redeemedBy: undefined,
          cursor,
          limit: 20,
          withProfiles: true,
        },
        options,
      );
      if (ok) {
        return handleFetchRedemptionResult(
          `${store}_${tabState}_admin`,
          cursor,
          data.cursor ?? null,
          data.rewardRedemptions,
          data.rewardOffers ?? [],
          isCompleted,
          store,
          data.profiles,
        );
      }

      return { shouldFetchMore: false };
    };

  const fetchRedemptionsByRedeemedBy: RewardStoreAction['fetchRedemptionsByRedeemedBy'] =
    async (store, tabState, redeemedBy, referralSystem, cursor, options) => {
      const { state, isCompleted } = RewardRedemption.tabStateToState(tabState);
      const { ok, data } = await restGetRewardRedemptions(
        { store },
        {
          state,
          redeemedBy: redeemedBy,
          referralSystem,
          cursor,
          limit: 20,
          withProfiles: true,
        },
        options,
      );
      if (ok) {
        return handleFetchRedemptionResult(
          `${store}_${tabState}_${redeemedBy}`,
          cursor,
          data.cursor ?? null,
          data.rewardRedemptions,
          data.rewardOffers ?? [],
          isCompleted,
          store,
          data.profiles,
        );
      }

      return { shouldFetchMore: false };
    };

  const fetchRedemptionsByOfferId: RewardStoreAction['fetchRedemptionsByOfferId'] =
    async (store, offerId, cursor, options) => {
      // const offer = State.getOffer(get(), offerId);
      // if (!offer) {
      //   return { shouldFetchMore: false };
      // }
      const { ok, data } = await restGetRewardRedemptions(
        { store },
        {
          rewardOfferId: offerId,
          redeemedBy: useSessionStore.getState().activeUserId ?? '',
          cursor,
          limit: 20,
        },
        options,
      );
      if (ok) {
        const oldCursor = cursor;
        const newCursor = data.cursor ?? null;
        const redemptions = data.rewardRedemptions.map(
          (r) => new RewardRedemption(r),
        );
        set((state) => {
          // redemption
          if (oldCursor === undefined) {
            State.setRedemptionsByOfferId(
              state,
              offerId,
              store,
              ...redemptions,
            );
          } else {
            State.pushRedemptionsByOfferId(
              state,
              offerId,
              store,
              ...redemptions,
            );
          }
          State.setRedemptionCursor(state, `${store}_${offerId}`, newCursor);
          return { ...state };
        });
        return {
          shouldFetchMore: !!newCursor && redemptions.length === 0,
        };
      }

      return { shouldFetchMore: false };
    };

  const fetchLastRecipient: RewardStoreAction['fetchLastRecipient'] = async (
    store,
    options,
  ) => {
    const { ok, data } = await restGetRewardRedemptions(
      { store },
      {
        redeemedBy: useSessionStore.getState().activeUserId ?? '',
        lastWithRecipient: true,
      },
      options,
    );
    if (ok) {
      const lastRecipient = data.lastRedemptionWithRecipient;
      return lastRecipient ? new RewardRedemption(lastRecipient) : undefined;
    }
    return undefined;
  };

  const fetchRedemption: RewardStoreAction['fetchRedemption'] = async (
    store,
    rewardRedemptionId,
    options,
  ) => {
    const { ok, data } = await restGetRewardRedemption(
      { store, rewardRedemptionId },
      options,
    );
    if (ok) {
      const redemption = data.rewardRedemption;
      return redemption ? new RewardRedemption(redemption) : undefined;
    }
    return undefined;
  };

  const createRedemption: RewardStoreAction['createRedemption'] = async (
    store,
    body,
    options,
  ) => {
    // const promise = {
    //   reject: () => {
    //     // do nothing
    //   },
    // };
    // const res: { id: string } = {
    //   id: '',
    // };
    // const websocketPromise = new Promise<void>((resolve, reject) => {
    //   const listener = (
    //     event: WebSocketPush<WebSocketTopicType.RewardRedemption>,
    //   ) => {
    //     const redemption = event.data.state[res.id];
    //     if (redemption) {
    //       if (
    //         redemption.state === RewardRedemptionState.Pending ||
    //         redemption.processedAt === undefined
    //       ) {
    //         // continue to wait
    //       } else if (redemption.state === RewardRedemptionState.Failed) {
    //         webSocketClient.removeTopicPushListener(
    //           WebSocketTopicType.RewardRedemption,
    //           listener,
    //         );
    //         reject(new Error('Fail to create redemption'));
    //       } else {
    //         webSocketClient.removeTopicPushListener(
    //           WebSocketTopicType.RewardRedemption,
    //           listener,
    //         );
    //         resolve();
    //       }
    //     }
    //   };
    //   promise.reject = () => {
    //     webSocketClient.removeTopicPushListener(
    //       WebSocketTopicType.RewardRedemption,
    //       listener,
    //     );
    //     reject(new Error());
    //   };
    //   webSocketClient.addTopicPushListener(
    //     WebSocketTopicType.RewardRedemption,
    //     listener,
    //   );
    // });
    try {
      const { ok, data, error } = await restCreateRewardRedmption(
        { store },
        body,
        options,
      );
      if (ok) {
        const redemption = data.rewardRedemption;
        // res.id = redemption.rewardRedemptionId;
        set((state) => {
          State.updateRedemption(state, new RewardRedemption(redemption));
          return { ...state };
        });
        // await websocketPromise;
        return new RewardRedemption(redemption);
      } else {
        throw error;
      }
    } catch (e) {
      if (e instanceof Error)
        console.error('Fail to create redemption', e.message);
      // promise.reject();
    }
    return undefined;
  };

  const captureRedemptionPayment: RewardStoreAction['captureRedemptionPayment'] =
    async (store, rewardRedemptionId, options) => {
      const { ok, data, error } = await restCaptureRewardRedemptionPayment(
        { store, rewardRedemptionId },
        options,
      );
      if (ok) {
        set((state) => {
          State.updateRedemption(
            state,
            new RewardRedemption(data.rewardRedemption),
          );
          return { ...state };
        });
        return true;
      } else {
        console.error('Fail to capture redemption payment', error.message);
        return false;
      }
    };

  const deliverPhysicalGood: RewardStoreAction['deliverPhysicalGood'] = async (
    store,
    rewardRedemptionId,
    body,
    options,
  ) => {
    const { ok, data, error } = await restFulfillPhysicalGoodRedemption(
      { store, rewardRedemptionId },
      body,
      options,
    );
    if (ok) {
      set((state) => {
        State.updateRedemption(
          state,
          new RewardRedemption(data.rewardRedemption),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to deliver redemption', error.message);
      return false;
    }
  };

  const extendCompletion: RewardStoreAction['extendCompletion'] = async (
    store,
    rewardRedemptionId,
    durationInMs,
    options,
  ) => {
    const { ok, data, error } = await restExtendRewardRedemptionCompletion(
      { store, rewardRedemptionId },
      { durationInMs },
      options,
    );
    if (ok) {
      set((state) => {
        State.updateRedemption(
          state,
          new RewardRedemption(data.rewardRedemption),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to extend redemption', error.message);
      return false;
    }
  };

  const completeRedemption: RewardStoreAction['completeRedemption'] = async (
    store,
    rewardRedemptionId,
    body,
    options,
  ) => {
    const { ok, data, error } = await restCompleteRewardRedemption(
      { store, rewardRedemptionId },
      body,
      options,
    );
    if (ok) {
      set((state) => {
        State.updateRedemption(
          state,
          new RewardRedemption(data.rewardRedemption),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to complete redemption', error.message);
      return false;
    }
  };

  const remindRedemption: RewardStoreAction['remindRedemption'] = async (
    store,
    rewardRedemptionId,
    options,
  ) => {
    const { ok, data, error } = await restRemindRewardRedemption(
      { store, rewardRedemptionId },
      options,
    );
    if (ok) {
      set((state) => {
        State.updateRedemption(
          state,
          new RewardRedemption(data.rewardRedemption),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to remind redemption', error.message);
      return false;
    }
  };

  const cancelRedemption: RewardStoreAction['cancelRedemption'] = async (
    store,
    rewardRedemptionId,
    options,
  ) => {
    const { ok, data, error } = await restCancelRewardRedemption(
      { store, rewardRedemptionId },
      options,
    );
    if (ok) {
      set((state) => {
        State.updateRedemption(
          state,
          new RewardRedemption(data.rewardRedemption),
        );
        return { ...state };
      });
      return true;
    } else {
      console.error('Fail to cancel redemption', error.message);
      return false;
    }
  };

  const cancelWebPlanSubscription: RewardStoreAction['cancelWebPlanSubscription'] =
    async (store, options) => {
      const { ok, error } = await restCancelWebPlanSubscription(
        { store },
        options,
      );
      if (ok) {
        // TODO: set account from data directly
        await useUserStore.getState().getCurrentUser(options);
        return true;
      } else {
        console.error('Fail to cancel web plan subscription', error.message);
        return false;
      }
    };

  const manageWebPlanSubscription: RewardStoreAction['manageWebPlanSubscription'] =
    async (store, options) => {
      const { ok, data, error } = await restManageWebPlanSubscription(
        { store },
        options,
      );
      if (ok) {
        return data.url;
      } else {
        console.error('Fail to manage web plan subscription', error.message);
        return undefined;
      }
    };

  const createRewardReferral: RewardStoreAction['createRewardReferral'] =
    async (path, body, options) => {
      const response = await restCreateRewardReferral(path, body, options);
      if (response.ok) {
        const rewardReferral = new RewardReferral(response.data.rewardReferral);

        return rewardReferral;
      }
      return undefined;
    };

  const referralCodeValidation: RewardStoreAction['referralCodeValidation'] =
    async (path, params, options) => {
      const response = await restReferralCodeValidation(path, params, options);
      if (response.ok) {
        if (response.data.profile) {
          const profile = new Profile(response.data.profile);
          return profile;
        }
        return ResetPasswordResult.Success;
      }
      return ResetPasswordResult.Error;
    };

  const getRewardReferralCode: RewardStoreAction['getRewardReferralCode'] =
    async (referralSystem) => {
      const { ok, data } = await restGetRewardReferralCode({ referralSystem });
      if (ok) {
        return data.rewardReferralCode;
      }
      return undefined;
    };

  const getRewardReferrals: RewardStoreAction['getRewardReferrals'] = (
    fieldName,
  ) => State.getRewardReferrals(get(), fieldName);

  const getRewardReferral: RewardStoreAction['getRewardReferral'] = (userId) =>
    State.getReferral(get(), userId);

  const fetchRewardReferrals: RewardStoreAction['fetchRewardReferrals'] =
    async ({ referralSystem, referredBy, fieldName }, cursor, options) => {
      const { ok, data } = await restGetRewardReferrals(
        { referralSystem: referralSystem },
        {
          referredBy,
          cursor,
          limit: 20,
          withProfiles: true,
        },
        options,
      );
      if (ok) {
        set((state) => {
          const newCursor = data.cursor ?? null;
          const rewardReferrals = data.rewardReferrals.map(
            (o) => new RewardReferral(o),
          );
          if (cursor === undefined) {
            State.setRewardReferrals(state, fieldName, ...rewardReferrals);
          } else {
            State.pushRewardReferrals(state, fieldName, ...rewardReferrals);
          }
          State.setRewardReferralCursor(state, fieldName, newCursor);

          return { ...state };
        });

        // add profiles
        useUserStore
          .getState()
          .addProfiles(
            UserListType.Referral,
            ...data.profiles.map((p) => new Profile(p.profile)),
          );
      }
      return {
        shouldFetchMore: !!data?.cursor && data?.rewardReferrals.length === 0,
      };
    };

  const fetchRewardReferral: RewardStoreAction['fetchRewardReferral'] = async (
    { referralSystem },
    options,
  ) => {
    const { ok, data } = await restGetRewardReferral(
      { referralSystem },
      { withProfiles: true },
      options,
    );
    if (ok) {
      const rewardReferral = data.rewardReferral;
      if (rewardReferral !== null) {
        set((state) => {
          State.addRewardReferralToMap(
            state,
            RewardReferralType.Referral,
            `${rewardReferral.referralSystem}_${rewardReferral.userId}`,
            new RewardReferral({
              ...rewardReferral,
              rewardReferralCode: data.rewardReferralCode,
            }),
          );
          return { ...state };
        });
      }

      // add profiles
      useUserStore
        .getState()
        .addProfiles(
          UserListType.Referral,
          ...data.profiles.map((p) => new Profile(p.profile)),
        );
    }
  };

  const fetchRewardReferralUser: RewardStoreAction['fetchRewardReferralUser'] =
    async (referralSystem, userId, options) => {
      const { ok, data } = await restGetRewardReferralUser(
        { referralSystem, userId },
        { withProfiles: true },
        options,
      );
      if (ok && data !== null) {
        const rewardReferral = data.rewardReferral;
        if (rewardReferral !== null) {
          set((state) => {
            State.addRewardReferralToMap(
              state,
              RewardReferralType.Referral,
              `${rewardReferral.referralSystem}_${rewardReferral.userId}`,
              new RewardReferral({
                ...rewardReferral,
                rewardReferralCode: data.rewardReferralCode,
              }),
            );
            return { ...state };
          });
        }

        // add profiles
        useUserStore
          .getState()
          .addProfiles(
            UserListType.Referral,
            ...data.profiles.map((p) => new Profile(p.profile)),
          );
      }
    };

  const onCategoryPrune: RewardStoreAction['onCategoryPrune'] = (category) => {
    set((state) => {
      State.deleteOfferList(
        state,
        `${category.store}_${category.rewardCategoryId}`,
      );
      State.deleteOfferList(
        state,
        `${category.store}_${category.rewardCategoryId}_admin`,
      );
      return { ...state };
    });
  };

  const onOfferPrune: RewardStoreAction['onOfferPrune'] = (offer) => {
    set((state) => {
      const fieldName = `${offer.store}_${offer.rewardOfferId}`;
      state.redemptions.clear(fieldName);
      const field = state.redemptions.list(fieldName);
      field.removeListener();
      return { ...state };
    });
  };

  const onRedemptionPrune: RewardStoreAction['onRedemptionPrune'] = (
    redemption,
  ) => {
    set((state) => {
      State.deleteOfferMapItem(
        state,
        RewardOfferMapType.ActiveUser,
        redemption.id(),
      );
      return { ...state };
    });
    useUserStore
      .getState()
      .deleteProfileFromMap(
        ProfileMapType.Redemption,
        redemption.rewardRedemptionId,
      );
    useUserStore
      .getState()
      .deleteProfileFromMap(
        ProfileMapType.Redemption,
        redemption.rewardOfferId,
      );
  };

  return {
    initialize,
    shutdown,
    categories: new RewardCategoryRepository(),
    offers: new RewardOfferRepository(),
    redemptions: new RewardRedemptionRepository(),
    referrals: new RewardReferralRepository(),
    getCategories,
    getCategory,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    getOffers,
    getOffer,
    fetchRecentOffers,
    fetchOffers,
    fetchOffersByAdmin,
    fetchOffer,
    fetchOfferByRedemptionId,
    getRedemptions,
    getRedemption,
    getRedemptionsByOfferId,
    fetchRedemptions,
    fetchRedemptionsByAdmin,
    fetchRedemptionsByRedeemedBy,
    fetchRedemptionsByOfferId,
    fetchLastRecipient,
    fetchRedemption,
    createRedemption,
    captureRedemptionPayment,
    deliverPhysicalGood,
    extendCompletion,
    completeRedemption,
    remindRedemption,
    cancelRedemption,
    cancelWebPlanSubscription,
    manageWebPlanSubscription,
    getRewardReferralCode,
    getRewardReferrals,
    getRewardReferral,
    fetchRewardReferrals,
    fetchRewardReferral,
    fetchRewardReferralUser,
    createRewardReferral,
    referralCodeValidation,
    onCategoryPrune,
    onOfferPrune,
    onRedemptionPrune,
  };
};

export const useRewardStore = createWithEqualityFn<Store>()(
  initializer,
  Object.is,
);
export const useRewardStoreListField = createStoreListFieldHook(useRewardStore);
export const useRewardStoreMapField = createStoreMapFieldHook(useRewardStore);
export const useRedemptionListField = (
  store: RewardPlaform,
  isAdmin = false,
) => {
  useRewardStoreListField(
    'redemptions',
    `${store}_${RewardRedemptionTabState.All}${isAdmin ? '_admin' : ''}`,
  );
  useRewardStoreListField(
    'redemptions',
    `${store}_${RewardRedemptionTabState.NotDelivered}${
      isAdmin ? '_admin' : ''
    }`,
  );
  useRewardStoreListField(
    'redemptions',
    `${store}_${RewardRedemptionTabState.Delivered}${isAdmin ? '_admin' : ''}`,
  );
  useRewardStoreListField(
    'redemptions',
    `${store}_${RewardRedemptionTabState.Completed}${isAdmin ? '_admin' : ''}`,
  );
  useRewardStoreListField(
    'redemptions',
    `${store}_${RewardRedemptionTabState.Refund}${isAdmin ? '_admin' : ''}`,
  );
};
